import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { SessionStorageService } from 'ngx-webstorage';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

import { StateStorageService } from 'app/core/auth/state-storage.service';
import { ApplicationConfigService } from '../config/application-config.service';
import { Account } from 'app/core/auth/account.model';
import { UserService } from '../user/user.service';
import { EncryptionUtilService } from '../utils/encryption.util.service';

@Injectable({ providedIn: 'root' })
export class AccountService {
	private resourceApiUrl = this.applicationConfigService.getEndpointFor('api/account');

	private userIdentity: Account | null = null;
	private authenticationState = new ReplaySubject<Account | null>(1);
	private accountCache$?: Observable<Account> | null;

	constructor(
		private translateService: TranslateService,
		private sessionStorageService: SessionStorageService,
		private http: HttpClient,
		private stateStorageService: StateStorageService,
		private router: Router,
		private applicationConfigService: ApplicationConfigService,
		private _userService: UserService,
		private _encryptionUtilService: EncryptionUtilService
	) {}

	save(account: Account): Observable<{}> {
		if (account.langKey && account.langKey instanceof Object) {
			account.langKey = account.langKey.id;
		}
		return this.http.post(this.resourceApiUrl, account);
	}

	authenticate(identity: Account | null): void {
		if (identity) {
			this._userService.setUser({
				id: identity.id,
				name: identity.firstName,
				email: identity.email,
				avatar: identity.imageUrl,
				color: identity.color,
			});
		}
		this.userIdentity = identity;
		this.authenticationState.next(this.userIdentity);
		if (!identity) {
			this.accountCache$ = null;
		}
	}

	hasAnyAuthority(authorities: string[] | string): boolean {
		if (!this.userIdentity) {
			return false;
		}
		if (!Array.isArray(authorities)) {
			authorities = [authorities];
		}
		return this.userIdentity.authorities.some((authority: string) =>
			authorities.includes(authority)
		);
	}

	identity(force?: boolean): Observable<Account | null> {
		if (!this.accountCache$ || force) {
			this.accountCache$ = this.fetch().pipe(
				tap((account: Account) => {
					account.authorities = account.authorities.map((item) =>
						this._encryptionUtilService.decrypt(item)
					);
					this.authenticate(account);

					// After retrieve the account info, the language will be changed to
					// the user's preferred language configured in the account setting
					// unless user have choosed other language in the current session
					if (!this.sessionStorageService.retrieve('locale')) {
						this.translateService.use(account.langKey);
					}

					this.navigateToStoredUrl();
				}),
				shareReplay()
			);
		}
		return this.accountCache$.pipe(catchError(() => of(null)));
	}

	isAuthenticated(): boolean {
		return this.userIdentity !== null;
	}

	getAuthenticationState(): Observable<Account | null> {
		return this.authenticationState.asObservable();
	}

	private fetch(): Observable<Account> {
		return this.http.get<Account>(this.resourceApiUrl);
	}

	private navigateToStoredUrl(): void {
		// previousState can be set in the authExpiredInterceptor and in the userRouteAccessService
		// if login is successful, go to stored previousState and clear previousState
		const previousUrl = this.stateStorageService.getUrl();
		if (previousUrl) {
			this.stateStorageService.clearUrl();
			this.router.navigateByUrl(previousUrl);
		}
	}
}
